import { useMemo } from "react";
import { formatChartValue } from "@scryerlabs/ui/charts";
import { useChartColors } from "@scryerlabs/ui";
import { Sparkline } from "../../../components/common/Sparkline";
import { useTradingStrategyChartContent } from "../../../api/hooks/useTradingStrategies";
import type { TradingStrategyDto } from "../../../api/hooks/useTradingStrategies";
import type { ChartColors } from "@scryerlabs/ui";

/**
 * The same tone-to-colour mapping the full chart makes, so one payload reads the same on a card as
 * it does on the detail page. The card used to hard-code the app's primary blue and ignore the
 * tone, which is why an equity curve was green in one place and blue in the other.
 */
function toneColour(tone: string | undefined, colors: ChartColors): string {
  switch (tone) {
    case "negative":
      return colors.negative;
    case "warning":
      return colors.warning;
    case "accent":
      return colors.accent;
    case "muted":
      return colors.muted;
    default:
      return colors.positive;
  }
}

/**
 * A strategy's published curve at card size: the native series, the alt symbol series where it has
 * one, and a legend naming them.
 *
 * Its own component because three card surfaces draw it and only one of them wants the figures
 * beside it - the browse grid and the dashboard strip go through StrategyPerformanceSummary, and a
 * subscription card shows the curve alone.
 */
export function StrategyCurve({
  strategy,
  emphasised = false,
}: {
  strategy: TradingStrategyDto;
  /** Lifts the curve out of its resting dimmed state, for a card that has a hover treatment. */
  emphasised?: boolean;
}) {
  const chartColors = useChartColors();

  // The first chart the back office published, whatever kind it is — ordered by displayOrder, so
  // [0] is the one a customer would land on from the detail page.
  const firstChart = strategy.charts[0];
  const { data: firstChartPayload } = useTradingStrategyChartContent(
    strategy.id,
    firstChart?.id
  );

  const curveData = useMemo(() => {
    const points = firstChartPayload?.series[0]?.points ?? [];
    const values = points.map((point) => Number(point.y));

    // The sparkline scales linearly between its own min and max, which is wrong for an equity
    // curve: ten years of compounding runs from 1x to well over 1000x, so every year before the
    // last sits on the floor and the whole card looks like a flat line with a spike. The payload
    // says when it wants a log axis — the detail chart honours it, and so must this.
    if (firstChartPayload?.yAxis?.scale !== "logarithmic") return values;

    // log10 of a non-positive number is not a number, and a curve is a multiple of the starting
    // balance so it should never be one. Guarded rather than trusted, because one NaN would break
    // the whole polyline rather than one point of it.
    return values.map((value) => (value > 0 ? Math.log10(value) : 0));
  }, [firstChartPayload]);

  // The alt symbol series, where the strategy has one. Same treatment as the curve above, except
  // that a null stays null: it is how the payload says the alt vehicle did not exist yet, and the
  // sparkline draws those as a gap rather than a line back to the floor.
  const comparisonSeries = firstChartPayload?.series[1];

  const comparisonData = useMemo(() => {
    const points = comparisonSeries?.points;
    if (!points) return undefined;

    const logarithmic = firstChartPayload?.yAxis?.scale === "logarithmic";

    return points.map((point) => {
      if (point.y === null || point.y === undefined) return null;

      const value = Number(point.y);
      if (!Number.isFinite(value)) return null;

      return logarithmic ? (value > 0 ? Math.log10(value) : null) : value;
    });
  }, [comparisonSeries, firstChartPayload]);

  // Read off the payload's own points and formatted with the same helper the full chart uses, so
  // a card and its detail page never disagree about what a point says. Deliberately NOT derived
  // from curveData, which is log-scaled for drawing.
  const readouts = useMemo(() => {
    const points = firstChartPayload?.series[0]?.points ?? [];
    const comparisonPoints = comparisonSeries?.points;

    return points.map((point, index) => {
      const comparison = comparisonPoints?.[index]?.y;

      return {
        label: String(point.x),
        value: formatChartValue(firstChartPayload?.yAxis?.format, Number(point.y)),
        comparisonValue:
          comparison === null || comparison === undefined
            ? undefined
            : formatChartValue(firstChartPayload?.yAxis?.format, Number(comparison)),
      };
    });
  }, [firstChartPayload, comparisonSeries]);

  const baseColour = toneColour(firstChartPayload?.series[0]?.tone, chartColors);
  const comparisonColour = toneColour(comparisonSeries?.tone, chartColors);

  return (
    <div
        className="my-3"
        style={{
          color: baseColour,
          opacity: emphasised ? 1 : 0.7,
          transition: "opacity 200ms ease",
        }}
      >
        {/* Drawn only where there is something real to draw. A strategy with no published chart
            shows no chart rather than an impression of one. */}
        {curveData.length > 0 && (
          <>
            <Sparkline
              data={curveData}
              readouts={readouts}
              height={64}
              comparison={comparisonData}
              comparisonColor={comparisonColour}
            />
            {/* Named, not just coloured. The readout is hover-only, so on a phone it does not
                exist - and an unexplained second line is worse than no second line. */}
            {comparisonSeries && (
              <div
                className="d-flex flex-wrap justify-content-center gap-3 mt-2 mb-2 text-body-secondary"
                style={{ fontSize: "0.65rem", opacity: 0.85 }}
              >
                <span className="d-inline-flex align-items-center gap-1">
                  {/* The line's own colour, not currentColor: this row is text-body-secondary, so
                      currentColor here is the muted grey of the label rather than the blue of the
                      curve, and the swatch stops matching the thing it names. */}
                  <span
                    aria-hidden
                    style={{
                      width: 8,
                      height: 2,
                      borderRadius: 1,
                      backgroundColor: baseColour,
                    }}
                  />
                  {firstChartPayload?.series[0]?.name}
                </span>
                <span className="d-inline-flex align-items-center gap-1">
                  <span
                    aria-hidden
                    style={{
                      width: 8,
                      height: 2,
                      borderRadius: 1,
                      backgroundColor: comparisonColour,
                    }}
                  />
                  {comparisonSeries.name}
                </span>
              </div>
            )}
          </>
        )}
    </div>
  );
}
